import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { NavItem } from './navConfig';
import { getActiveNavHref } from './navConfig';
import UpgradePrompt from '../UpgradePrompt';

interface SidebarNavItemProps {
  item: NavItem;
  pendingCount?: number;
  locked?: boolean;
}

export default function SidebarNavItem({ item, pendingCount = 0, locked = false }: SidebarNavItemProps) {
  const location = useLocation();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const activeHref = getActiveNavHref(location.pathname);
  const isActive = !locked && activeHref === item.href;

  if (locked) {
    return (
      <>
        <button
          onClick={() => setShowUpgrade(true)}
          className="group flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-sm font-medium transition-colors text-ink-4 hover:bg-panel-2 hover:text-ink-3"
        >
          <item.icon className="h-5 w-5 shrink-0 text-ink-4 transition-colors group-hover:text-ink-3" />
          <span className="truncate">{item.name}</span>
          <LockClosedIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-ink-4" />
        </button>
        <UpgradePrompt
          isOpen={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          feature={item.requiredFeature}
        />
      </>
    );
  }

  return (
    <Link
      to={item.href}
      className={clsx(
        'group flex items-center gap-x-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-teal-50 text-ink'
          : 'text-ink-2 hover:bg-panel-2 hover:text-ink'
      )}
    >
      <item.icon
        className={clsx(
          'h-5 w-5 shrink-0 transition-colors',
          isActive ? 'text-teal-600' : 'text-ink-4 group-hover:text-ink-2'
        )}
      />
      <span className="truncate">{item.name}</span>
      {item.badge === 'pending-reviews' && pendingCount > 0 && (
        <span className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white">
          {pendingCount > 99 ? '99+' : pendingCount}
        </span>
      )}
    </Link>
  );
}
